import { useMemo, useState } from "react";

import { queryGetNotes } from "@type/api/management/getNotesHistory";
import { useTranslationContext } from "context/TranslationContext";
import { formatJSTDateFromBE } from "helpers/formatJSTDateFromBE";
import { getTranslationKey } from "helpers/getTranslationKey";
import { useQueryWithCallbacks } from "lib/query/useQueryWithCallbacks";

import { TGetNotesReq, TGetNotesRes } from "@type/api/management/types";
import { MISSING_TRANSLATION_MESSAGE } from "constants/index";

interface UseGetNotesParams {
  params: TGetNotesReq;
  onSuccess?: (res: TGetNotesRes) => void;
}

export const useGetNotes = ({ params, onSuccess }: UseGetNotesParams) => {
  const [notes, setNotes] = useState<TGetNotesRes["data"]>([]);
  const { language } = useTranslationContext();
  const queryGetNotesKey = useMemo(() => {
    return queryGetNotes._key(params);
  }, [params]);

  const { isLoading: isLoadingGetNotes, refetch: refetchNotes } =
    useQueryWithCallbacks({
      queryKey: queryGetNotesKey,
      enabled: !!params?.data?.page && !!params?.data?.pageSize,
      queryFn: () => queryGetNotes(params),
      onSuccess: (res) => {
        onSuccess?.(res);
        setNotes(res.data);
      },
    });

  const formattedNotes = useMemo(
    () =>
      notes.map((data) => ({
        ...data,
        dateAndTime: formatJSTDateFromBE(
          data.updatedAt || data.createdAt,
          language
        ),
        pipelineStatusName: getTranslationKey(
          data.pipelineStatus?.name
            ?.toLowerCase()
            ?.replace(MISSING_TRANSLATION_MESSAGE, "")
            ?.trim()
        ),
        ticketName: data.ticket
          ? `${data.ticket.code} - ${data.ticket.title}`
          : "-",
        jobListing:
          data.ticket?.clientEngagementData?.corporation?.name ||
          data.ticket?.staffAndClientMatchingData?.joblisting?.title,
        submitterName: data.submitter?.fullName,
        submitterProfilePicture: data.submitter?.profilePicture,
      })),
    [language, notes]
  );

  return {
    queryGetNotesKey,
    notes: formattedNotes,
    isLoadingGetNotes,
    refetchNotes,
  };
};
